import {
  getThreads as getThreadsAPI,
  createThread as createThreadAPI,
  updateThread as updateThreadAPI,
  deleteThread as deleteThreadAPI,
  getMessages as getMessagesAPI,
  clearMessages as clearMessagesAPI,
  getPromptConfigs as getPromptConfigsAPI,
  createPromptConfig as createPromptConfigAPI,
  sendChatMessage as sendChatMessageAPI,
  runDatakeeper as runDatakeeperAPI,
  applyBioUpdate as applyBioUpdateAPI,
  saveSession as saveSessionAPI,
  getAllSessions as getAllSessionsAPI,
  getGoldenSessions as getGoldenSessionsAPI,
  getDefaultPrompts as getDefaultPromptsAPI,
  getAvailableModels as getAvailableModelsAPI,
  searchProducts as searchProductsAPI
} from '../api/playground';

// Action Types
export const PLAYGROUND_SET_LOADING = 'PLAYGROUND_SET_LOADING';
export const PLAYGROUND_SET_ERROR = 'PLAYGROUND_SET_ERROR';
export const PLAYGROUND_SET_THREADS = 'PLAYGROUND_SET_THREADS';
export const PLAYGROUND_SELECT_THREAD = 'PLAYGROUND_SELECT_THREAD';
export const PLAYGROUND_UPDATE_THREAD = 'PLAYGROUND_UPDATE_THREAD';
export const PLAYGROUND_ADD_THREAD = 'PLAYGROUND_ADD_THREAD';
export const PLAYGROUND_REMOVE_THREAD = 'PLAYGROUND_REMOVE_THREAD';
export const PLAYGROUND_SET_MESSAGES = 'PLAYGROUND_SET_MESSAGES';
export const PLAYGROUND_ADD_MESSAGE = 'PLAYGROUND_ADD_MESSAGE';
export const PLAYGROUND_CLEAR_CONVERSATION = 'PLAYGROUND_CLEAR_CONVERSATION';
export const PLAYGROUND_SET_BRIEF = 'PLAYGROUND_SET_BRIEF';
export const PLAYGROUND_SET_LLM_CONTEXT = 'PLAYGROUND_SET_LLM_CONTEXT';
export const PLAYGROUND_SET_MODEL = 'PLAYGROUND_SET_MODEL';
export const PLAYGROUND_SET_MODELS = 'PLAYGROUND_SET_MODELS';
export const PLAYGROUND_SET_DISPATCHER_PROMPT = 'PLAYGROUND_SET_DISPATCHER_PROMPT';
export const PLAYGROUND_SET_DATAKEEPER_PROMPT = 'PLAYGROUND_SET_DATAKEEPER_PROMPT';
export const PLAYGROUND_SET_PROMPT_CONFIGS = 'PLAYGROUND_SET_PROMPT_CONFIGS';
export const PLAYGROUND_SET_DATAKEEPER_RESULT = 'PLAYGROUND_SET_DATAKEEPER_RESULT';
export const PLAYGROUND_SET_BOTTOM_PANEL_HEIGHT = 'PLAYGROUND_SET_BOTTOM_PANEL_HEIGHT';
export const PLAYGROUND_SET_BOTTOM_PANEL_COLLAPSED = 'PLAYGROUND_SET_BOTTOM_PANEL_COLLAPSED';
export const PLAYGROUND_SET_LAST_SAVED_SESSION = 'PLAYGROUND_SET_LAST_SAVED_SESSION';
export const PLAYGROUND_SET_SESSIONS = 'PLAYGROUND_SET_SESSIONS';
export const PLAYGROUND_SET_SESSIONS_LOADING = 'PLAYGROUND_SET_SESSIONS_LOADING';
export const PLAYGROUND_TRUNCATE_CONVERSATION = 'PLAYGROUND_TRUNCATE_CONVERSATION';
export const PLAYGROUND_SET_DATAKEEPER_LOADING = 'PLAYGROUND_SET_DATAKEEPER_LOADING';
export const PLAYGROUND_SET_PRODUCT_RECS = 'PLAYGROUND_SET_PRODUCT_RECS';
export const PLAYGROUND_SET_PRODUCT_RECS_LOADING = 'PLAYGROUND_SET_PRODUCT_RECS_LOADING';

// Action Creators
export const fetchThreads = () => async dispatch => {
  dispatch({ type: PLAYGROUND_SET_LOADING, isLoading: true });
  try {
    const resp = await getThreadsAPI();
    dispatch({ type: PLAYGROUND_SET_THREADS, threads: resp.threads || [] });
  } catch (error) {
    dispatch({ type: PLAYGROUND_SET_ERROR, error });
  }
  dispatch({ type: PLAYGROUND_SET_LOADING, isLoading: false });
};

export const createThread = data => async dispatch => {
  const resp = await createThreadAPI(data);
  dispatch({ type: PLAYGROUND_ADD_THREAD, thread: resp.thread });
  return resp.thread;
};

export const updateThread = (id, data) => async dispatch => {
  const resp = await updateThreadAPI(id, data);
  dispatch({ type: PLAYGROUND_UPDATE_THREAD, thread: resp.thread });
  return resp.thread;
};

export const deleteThread = id => async dispatch => {
  await deleteThreadAPI(id);
  dispatch({ type: PLAYGROUND_REMOVE_THREAD, threadId: id });
};

export const selectThread = thread => async dispatch => {
  dispatch({ type: PLAYGROUND_SELECT_THREAD, thread });
  if (thread) {
    // Load messages for the selected thread
    try {
      const resp = await getMessagesAPI(thread.id);
      const messages = (resp.messages || []).map(msg => ({
        id: msg.id,
        role: msg.senderType === 'user' ? 'user' : 'assistant',
        content: msg.content,
        timestamp: msg.createdAt
      }));
      dispatch({ type: PLAYGROUND_SET_MESSAGES, messages });
    } catch (error) {
      console.error('Failed to load messages:', error);
      dispatch({ type: PLAYGROUND_SET_MESSAGES, messages: [] });
    }
  }
};

export const sendMessage = ({ threadId, message, dispatcherPrompt, model }) => async dispatch => {
  dispatch({ type: PLAYGROUND_ADD_MESSAGE, message: { role: 'user', content: message, timestamp: new Date().toISOString() } });
  dispatch({ type: PLAYGROUND_SET_LOADING, isLoading: true });
  try {
    const resp = await sendChatMessageAPI({ threadId, message, dispatcherPrompt, model });
    dispatch({ type: PLAYGROUND_ADD_MESSAGE, message: { role: 'assistant', content: resp.response, timestamp: new Date().toISOString() } });
    dispatch({ type: PLAYGROUND_SET_LLM_CONTEXT, llmContext: { ...resp.llmContext, tokenCount: resp.tokenCount, latencyMs: resp.latencyMs } });
    if (resp.briefComplete) {
      dispatch({ type: PLAYGROUND_SET_BRIEF, brief: resp.brief, briefComplete: true });
    }
    return resp;
  } catch (error) {
    dispatch({ type: PLAYGROUND_SET_ERROR, error });
    throw error;
  } finally {
    dispatch({ type: PLAYGROUND_SET_LOADING, isLoading: false });
  }
};

export const clearConversation = threadId => async dispatch => {
  if (threadId) {
    try {
      await clearMessagesAPI(threadId);
    } catch (error) {
      console.error('Failed to clear messages on server:', error);
    }
  }
  dispatch({ type: PLAYGROUND_CLEAR_CONVERSATION });
};

export const setDispatcherPrompt = prompt => ({ type: PLAYGROUND_SET_DISPATCHER_PROMPT, prompt });
export const setDatakeeperPrompt = prompt => ({ type: PLAYGROUND_SET_DATAKEEPER_PROMPT, prompt });
export const setSelectedModel = model => ({ type: PLAYGROUND_SET_MODEL, model });

export const fetchAvailableModels = () => async dispatch => {
  try {
    const resp = await getAvailableModelsAPI();
    dispatch({ type: PLAYGROUND_SET_MODELS, models: resp.models || [] });
  } catch (error) {
    console.error('Failed to fetch models:', error);
  }
};

export const fetchDefaultPrompts = () => async dispatch => {
  try {
    const resp = await getDefaultPromptsAPI();
    dispatch({ type: PLAYGROUND_SET_DISPATCHER_PROMPT, prompt: resp.dispatcherPrompt });
    dispatch({ type: PLAYGROUND_SET_DATAKEEPER_PROMPT, prompt: resp.datakeeperPrompt });
  } catch (error) {
    console.error('Failed to fetch default prompts:', error);
  }
};

export const runDatakeeper = data => async dispatch => {
  dispatch({ type: PLAYGROUND_SET_DATAKEEPER_LOADING, loading: true });
  try {
    const resp = await runDatakeeperAPI(data);
    dispatch({
      type: PLAYGROUND_SET_DATAKEEPER_RESULT,
      datakeeperResult: {
        action: resp.action,
        reasoning: resp.reasoning,
        updatedBio: resp.updatedBio,
        llmContext: resp.llmContext,
        tokenCount: resp.tokenCount,
        latencyMs: resp.latencyMs
      }
    });
    return resp;
  } catch (error) {
    dispatch({ type: PLAYGROUND_SET_ERROR, error });
    throw error;
  } finally {
    dispatch({ type: PLAYGROUND_SET_DATAKEEPER_LOADING, loading: false });
  }
};

export const applyBioUpdate = data => async dispatch => {
  const resp = await applyBioUpdateAPI(data);
  dispatch({ type: PLAYGROUND_UPDATE_THREAD, thread: resp.thread });
  return resp.thread;
};

export const saveSession = data => async dispatch => {
  const resp = await saveSessionAPI(data);
  dispatch({ type: PLAYGROUND_SET_LAST_SAVED_SESSION, session: resp.session });
  // Also update the thread in the list since grading fields may have changed
  dispatch({ type: PLAYGROUND_UPDATE_THREAD, thread: resp.session });
  return resp.session;
};

export const fetchPromptConfigs = (type = null) => async dispatch => {
  try {
    const resp = await getPromptConfigsAPI(type);
    dispatch({ type: PLAYGROUND_SET_PROMPT_CONFIGS, configs: resp.configs || [] });
  } catch (error) {
    console.error('Failed to fetch prompt configs:', error);
  }
};

export const savePromptConfig = data => async dispatch => {
  try {
    await createPromptConfigAPI(data);
    dispatch(fetchPromptConfigs());
  } catch (error) {
    const message = error?.response?.data?.error || error?.message || 'Failed to save config';
    throw new Error(message);
  }
};

export const fetchSessions = () => async dispatch => {
  dispatch({ type: PLAYGROUND_SET_SESSIONS_LOADING, loading: true });
  try {
    const resp = await getAllSessionsAPI();
    dispatch({ type: PLAYGROUND_SET_SESSIONS, sessions: resp.sessions || [] });
  } catch {
    try {
      const resp = await getGoldenSessionsAPI();
      dispatch({ type: PLAYGROUND_SET_SESSIONS, sessions: resp.sessions || [] });
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }
  dispatch({ type: PLAYGROUND_SET_SESSIONS_LOADING, loading: false });
};

export const regenerateMessage = ({ messageIndex, threadId, dispatcherPrompt, model }) => async (dispatch, getState) => {
  const { playground } = getState();
  const { messages } = playground;

  // The message at messageIndex is the assistant message to regenerate.
  // The user message that triggered it is at messageIndex - 1.
  const userMsg = messages[messageIndex - 1];
  if (!userMsg || userMsg.role !== 'user') return;

  // Note: For DB-backed messages, we'd need to clear messages on the server and re-send
  // For now, we truncate locally and re-send
  dispatch({ type: PLAYGROUND_TRUNCATE_CONVERSATION, index: messageIndex - 1 });

  // Re-send the user message to get a fresh response
  return dispatch(
    sendMessage({
      threadId,
      message: userMsg.content,
      dispatcherPrompt,
      model
    })
  );
};

export const fetchProductRecs = query => async dispatch => {
  dispatch({ type: PLAYGROUND_SET_PRODUCT_RECS_LOADING, loading: true });
  try {
    const resp = await searchProductsAPI({ query, limit: 10 });
    dispatch({ type: PLAYGROUND_SET_PRODUCT_RECS, productRecs: resp.products || [] });
  } catch (error) {
    dispatch({ type: PLAYGROUND_SET_PRODUCT_RECS, productRecs: [] });
  } finally {
    dispatch({ type: PLAYGROUND_SET_PRODUCT_RECS_LOADING, loading: false });
  }
};

export const setBottomPanelHeight = height => ({ type: PLAYGROUND_SET_BOTTOM_PANEL_HEIGHT, height });
export const setBottomPanelCollapsed = collapsed => ({ type: PLAYGROUND_SET_BOTTOM_PANEL_COLLAPSED, collapsed });
